import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'
import FieldHelp from '../../components/FieldHelp'

interface PurchaseOrder {
  id: string
  orderNumber: string
  orderDate: string
  expectedDate?: string
  supplier: string
  status: string
  items?: Array<{
    id: string
    description: string
    quantity: string
    receivedQuantity: string
    unitPrice: string
    account?: {
      code: string
      name: string
    }
  }>
}

interface GRN {
  id: string
  grnNumber: string
  grnDate: string
  purchaseOrderId: string
  supplier: string
  status: string
  notes?: string
  purchaseOrder?: {
    id: string
    orderNumber: string
    orderDate: string
    expectedDate?: string
    status: string
  }
  items?: Array<{
    id: string
    purchaseOrderItemId: string
    quantity: string
    acceptedQuantity: string
    rejectedQuantity: string
    description: string
    unitPrice: string
    notes?: string
    purchaseOrderItem?: {
      id: string
      description: string
      quantity: string
      receivedQuantity: string
      unitPrice: string
      account?: {
        code: string
        name: string
      }
    }
  }>
  purchaseInvoices?: Array<{
    id: string
    invoiceNumber: string
    invoiceDate: string
    total: string
    status: string
  }>
}

const GRNs: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission, loading: permissionsLoading } = usePermissions(user)
  const [grns, setGrns] = useState<GRN[]>([])
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedGRN, setSelectedGRN] = useState<GRN | null>(null)
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null)
  const [formData, setFormData] = useState({
    grnDate: new Date().toISOString().split('T')[0],
    purchaseOrderId: '',
    notes: '',
    items: [] as Array<{
      purchaseOrderItemId: string
      quantity: string
      acceptedQuantity: string
      rejectedQuantity: string
      notes: string
    }>,
  })

  useEffect(() => {
    fetchGRNs()
    fetchPurchaseOrders()
  }, [])

  const fetchGRNs = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/grns')
      setGrns(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching GRNs:', error)
      toast.error('Failed to load GRNs')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchPurchaseOrders = async () => {
    try {
      const response = await axios.get('/api/erp/purchase-orders')
      // Filter on frontend for approved/ordered/partially received
      const filtered = (response.data.data || []).filter((po: PurchaseOrder) => 
        ['APPROVED', 'ORDERED', 'PARTIALLY_RECEIVED'].includes(po.status)
      )
      setPurchaseOrders(filtered)
    } catch (error) {
      console.error('Error fetching purchase orders:', error)
    }
  }

  const handlePOSelect = async (poId: string) => {
    if (!poId) {
      setSelectedPO(null)
      setFormData(prev => ({ ...prev, purchaseOrderId: '', items: [] }))
      return
    }

    try {
      const response = await axios.get(`/api/erp/purchase-orders/${poId}`)
      const po = response.data.data
      setSelectedPO(po)

      // Initialize form items from PO items (only those not fully received)
      const items = (po.items || []).map((item: any) => {
        const remainingQty = parseFloat(item.quantity) - parseFloat(item.receivedQuantity || '0')
        return {
          purchaseOrderItemId: item.id,
          quantity: remainingQty > 0 ? remainingQty.toString() : '0',
          acceptedQuantity: remainingQty > 0 ? remainingQty.toString() : '0',
          rejectedQuantity: '0',
          notes: '',
        }
      }).filter((item: any) => parseFloat(item.quantity) > 0)

      setFormData(prev => ({
        ...prev,
        purchaseOrderId: poId,
        items,
      }))
    } catch (error: any) {
      console.error('Error fetching purchase order:', error)
      toast.error('Failed to load purchase order')
    }
  }

  const handleItemChange = (index: number, field: string, value: string) => {
    const newItems = [...formData.items]
    newItems[index] = { ...newItems[index], [field]: value }
    
    // Auto-calculate accepted quantity if quantity changes
    if (field === 'quantity') {
      const qty = parseFloat(value) || 0
      const rejected = parseFloat(newItems[index].rejectedQuantity) || 0
      newItems[index].acceptedQuantity = Math.max(0, qty - rejected).toString()
    }
    
    // Auto-adjust accepted quantity if rejected changes
    if (field === 'rejectedQuantity') {
      const qty = parseFloat(newItems[index].quantity) || 0
      const rejected = parseFloat(value) || 0
      newItems[index].acceptedQuantity = Math.max(0, qty - rejected).toString()
    }

    setFormData(prev => ({ ...prev, items: newItems }))
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.purchaseOrderId) {
      toast.error('Please select a purchase order')
      return
    }

    if (!formData.items || formData.items.length === 0) {
      toast.error('Please add at least one item')
      return
    }

    try {
      await axios.post('/api/erp/grns', {
        grnDate: formData.grnDate,
        purchaseOrderId: formData.purchaseOrderId,
        items: formData.items.map(item => ({
          purchaseOrderItemId: item.purchaseOrderItemId,
          quantity: parseFloat(item.quantity) || 0,
          acceptedQuantity: parseFloat(item.acceptedQuantity) || 0,
          rejectedQuantity: parseFloat(item.rejectedQuantity) || 0,
          notes: item.notes || undefined,
        })),
        notes: formData.notes || undefined,
      })
      toast.success('GRN created successfully!')
      setShowCreateModal(false)
      setFormData({
        grnDate: new Date().toISOString().split('T')[0],
        purchaseOrderId: '',
        notes: '',
        items: [],
      })
      setSelectedPO(null)
      fetchGRNs()
      fetchPurchaseOrders()
    } catch (error: any) {
      console.error('Error creating GRN:', error)
      toast.error(error.response?.data?.error || 'Failed to create GRN')
    }
  }

  const handleView = async (id: string) => {
    try {
      const response = await axios.get(`/api/erp/grns/${id}`)
      setSelectedGRN(response.data.data)
      setShowViewModal(true)
    } catch (error: any) {
      console.error('Error fetching GRN:', error)
      toast.error('Failed to load GRN')
    }
  }

  const handleSubmit = async (id: string) => {
    try {
      await axios.post(`/api/erp/grns/${id}/submit`)
      toast.success('GRN submitted')
      fetchGRNs()
    } catch (error: any) {
      console.error('Error submitting GRN:', error)
      toast.error(error.response?.data?.error || 'Failed to submit GRN')
    }
  }

  const handleReceive = async (id: string) => {
    try {
      await axios.post(`/api/erp/grns/${id}/receive`)
      toast.success('GRN received - PO quantities updated')
      fetchGRNs()
      fetchPurchaseOrders()
    } catch (error: any) {
      console.error('Error receiving GRN:', error)
      toast.error(error.response?.data?.error || 'Failed to receive GRN')
    }
  }

  const handleCancel = async (id: string) => {
    if (!confirm('Are you sure you want to cancel this GRN?')) {
      return
    }

    try {
      await axios.post(`/api/erp/grns/${id}/cancel`)
      toast.success('GRN cancelled')
      fetchGRNs()
    } catch (error: any) {
      console.error('Error cancelling GRN:', error)
      toast.error(error.response?.data?.error || 'Failed to cancel GRN')
    }
  }

  const handleCreateInvoice = async (id: string) => {
    try {
      const response = await axios.post(`/api/erp/grns/${id}/create-invoice`, {
        invoiceDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        postingDate: new Date().toISOString().split('T')[0],
      })
      toast.success('Purchase invoice created from GRN!')
      setShowViewModal(false)
      window.location.href = `/erp/purchase-invoices`
    } catch (error: any) {
      console.error('Error creating purchase invoice:', error)
      toast.error(error.response?.data?.error || 'Failed to create purchase invoice from GRN')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800'
      case 'SUBMITTED':
        return 'bg-yellow-100 text-yellow-800'
      case 'RECEIVED':
        return 'bg-green-100 text-green-800'
      case 'CANCELLED':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (permissionsLoading) {
    return <div className="p-6">Loading permissions...</div>
  }

  if (!hasPermission('erp_purchase_invoice', 'read')) {
    return <div className="p-6">You don't have permission to view GRNs.</div>
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <h1 className="text-2xl font-bold text-gray-900">Goods Receipt Notes (GRN)</h1>
          <PageHelp pageIdentifier="grns" title="Goods Receipt Notes" />
        </div>
        {hasPermission('erp_purchase_invoice', 'write') && (
          <button
            onClick={() => {
              setFormData({
                grnDate: new Date().toISOString().split('T')[0],
                purchaseOrderId: '',
                notes: '',
                items: [],
              })
              setSelectedPO(null)
              setShowCreateModal(true)
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create GRN
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading GRNs...</p>
        </div>
      ) : grns.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-600">No GRNs found.</p>
          {hasPermission('erp_purchase_invoice', 'write') && (
            <button
              onClick={() => {
                setFormData({
                  grnDate: new Date().toISOString().split('T')[0],
                  purchaseOrderId: '',
                  notes: '',
                  items: [],
                })
                setSelectedPO(null)
                setShowCreateModal(true)
              }}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Your First GRN
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GRN Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Purchase Order</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GRN Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {grns.map((grn) => (
                <tr key={grn.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{grn.grnNumber}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{grn.purchaseOrder?.orderNumber || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{grn.supplier}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{new Date(grn.grnDate).toLocaleDateString()}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(grn.status)}`}>
                      {grn.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleView(grn.id)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      View
                    </button>
                    {grn.status === 'DRAFT' && hasPermission('erp_purchase_invoice', 'write') && (
                      <button
                        onClick={() => handleSubmit(grn.id)}
                        className="text-yellow-600 hover:text-yellow-900 mr-4"
                      >
                        Submit
                      </button>
                    )}
                    {grn.status === 'SUBMITTED' && hasPermission('erp_purchase_invoice', 'write') && (
                      <button
                        onClick={() => handleReceive(grn.id)}
                        className="text-green-600 hover:text-green-900 mr-4"
                      >
                        Receive
                      </button>
                    )}
                    {grn.status === 'RECEIVED' && hasPermission('erp_purchase_invoice', 'write') && (
                      <button
                        onClick={() => handleCreateInvoice(grn.id)}
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                      >
                        Create Invoice
                      </button>
                    )}
                    {grn.status !== 'CANCELLED' && hasPermission('erp_purchase_invoice', 'write') && (
                      <button
                        onClick={() => handleCancel(grn.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Create GRN</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setSelectedPO(null)
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      GRN Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="grnDate"
                      value={formData.grnDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, grnDate: e.target.value }))}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Purchase Order <span className="text-red-500">*</span>
                      <FieldHelp content="Select the purchase order for which goods have been received. Only approved or ordered POs are available." />
                    </label>
                    <select
                      name="purchaseOrderId"
                      value={formData.purchaseOrderId}
                      onChange={(e) => handlePOSelect(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">-- Select Purchase Order --</option>
                      {purchaseOrders.map((po) => (
                        <option key={po.id} value={po.id}>
                          {po.orderNumber} - {po.supplier} ({new Date(po.orderDate).toLocaleDateString()})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {selectedPO && formData.items.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Items Received <span className="text-red-500">*</span>
                      <FieldHelp content="Enter the quantities received for each item. You can accept or reject partial quantities. Only accepted quantities will be invoiced." />
                    </label>
                    <div className="space-y-3">
                      {formData.items.map((item, index) => {
                        const poItem = selectedPO.items?.find(i => i.id === item.purchaseOrderItemId)
                        if (!poItem) return null

                        const remainingQty = parseFloat(poItem.quantity) - parseFloat(poItem.receivedQuantity || '0')
                        
                        return (
                          <div key={item.purchaseOrderItemId} className="p-4 border border-gray-200 rounded-lg">
                            <div className="mb-2">
                              <div className="text-sm font-medium text-gray-900">{poItem.description}</div>
                              <div className="text-xs text-gray-500">
                                Ordered: {parseFloat(poItem.quantity).toFixed(2)} | 
                                Already Received: {parseFloat(poItem.receivedQuantity || '0').toFixed(2)} | 
                                Remaining: {remainingQty.toFixed(2)}
                              </div>
                              {poItem.account && (
                                <div className="text-xs text-gray-500">
                                  Account: {poItem.account.code} - {poItem.account.name}
                                </div>
                              )}
                            </div>
                            <div className="grid grid-cols-4 gap-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Quantity Received</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max={remainingQty}
                                  value={item.quantity}
                                  onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Accepted</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={item.acceptedQuantity}
                                  onChange={(e) => handleItemChange(index, 'acceptedQuantity', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Rejected</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={item.rejectedQuantity}
                                  onChange={(e) => handleItemChange(index, 'rejectedQuantity', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                                <input
                                  type="text"
                                  value={item.notes}
                                  onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                  placeholder="Item notes"
                                />
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows={3}
                    placeholder="Additional notes about this GRN"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false)
                    setSelectedPO(null)
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Create GRN
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedGRN && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{selectedGRN.grnNumber}</h2>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Purchase Order</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedGRN.purchaseOrder?.orderNumber || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Supplier</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedGRN.supplier}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">GRN Date</label>
                  <p className="mt-1 text-sm text-gray-900">{new Date(selectedGRN.grnDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Status</label>
                  <p className="mt-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(selectedGRN.status)}`}>
                      {selectedGRN.status}
                    </span>
                  </p>
                </div>
              </div>

              {selectedGRN.items && selectedGRN.items.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Items</label>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Received</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Accepted</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rejected</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedGRN.items.map((item) => (
                          <tr key={item.id}>
                            <td className="px-4 py-2 text-sm text-gray-900">{item.description}</td>
                            <td className="px-4 py-2 text-sm text-gray-500">{parseFloat(item.quantity).toFixed(2)}</td>
                            <td className="px-4 py-2 text-sm text-green-600 font-medium">{parseFloat(item.acceptedQuantity).toFixed(2)}</td>
                            <td className="px-4 py-2 text-sm text-red-600">{parseFloat(item.rejectedQuantity).toFixed(2)}</td>
                            <td className="px-4 py-2 text-sm text-gray-500">
                              {parseFloat(item.unitPrice).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {selectedGRN.purchaseInvoices && selectedGRN.purchaseInvoices.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Purchase Invoices</label>
                  <div className="space-y-2">
                    {selectedGRN.purchaseInvoices.map((invoice) => (
                      <div key={invoice.id} className="p-3 bg-gray-50 rounded">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium">{invoice.invoiceNumber}</span>
                          <span className="text-sm text-gray-500">{new Date(invoice.invoiceDate).toLocaleDateString()}</span>
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          {parseFloat(invoice.total).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })} - {invoice.status}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedGRN.notes && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Notes</label>
                  <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{selectedGRN.notes}</p>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              {selectedGRN.status === 'RECEIVED' && hasPermission('erp_purchase_invoice', 'write') && (
                <button
                  onClick={() => handleCreateInvoice(selectedGRN.id)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Create Purchase Invoice
                </button>
              )}
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GRNs
